'use client';
import { Avatar, Link, Numeric } from '@/components/ui';
import { useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { DocumentTextIcon } from '@heroicons/react/24/outline';
import PlayerCard from '@/components/player/PlayerCard';
import { useTranslations } from 'next-intl';
import { useNameFormatting } from '@/contexts/NameFormattingContext';
import { type TeamEntry } from '@/app/[locale]/event/[eventId]/libs/teamLabels';
import { resolveParticipantLabel } from '@/app/[locale]/event/[eventId]/libs/participantLabel';
import { NAME_WRAP } from '@/app/[locale]/event/[eventId]/libs/nameBreaks';
import { NameText } from '@/app/[locale]/event/[eventId]/[slug]/components/NameText';

interface DrawSeedingsProps {
  playerHref?: (playerId: string) => string | undefined;
  seedings: Array<{
    seedNumber: number | null;
    drawPosition: number | null;
    pointsAtSeeding: number | null;
    pointsType: string | null;
    manualOverride: boolean | null;
    player: {
      id: string;
      firstName: string;
      lastName: string;
      username: string | null;
      /** A trimmed, non-empty alias beats the personal name (product ruling 2026-09-21). */
      alias?: string | null;
      currentRating: number | null;
      profilePhoto: string | null;
    };
    /** Team-division entry for this seed's player; null when nothing was typed. */
    team?: TeamEntry | null;
  }>;
  showRatings?: boolean;
  /** Use the enclosing public draw panel instead of a nested card. */
  embedded?: boolean;
  /** True for a team division (`event_group.is_team`) — every row is rendered as
   *  a team row (name fallback + roster), never PlayerCard, regardless of
   *  whether this particular registrant typed a team name/roster. */
  isTeamDivision?: boolean;
}

export function DrawSeedings({
  seedings,
  playerHref,
  showRatings = true,
  embedded = false,
  isTeamDivision = false,
}: DrawSeedingsProps) {
  const t = useTranslations('Event.drawView');
  const Panel = embedded ? 'div' : Card;
  const PanelContent = embedded ? 'div' : CardContent;
  const tMatches = useTranslations('Matches');
  const { isJapanese } = useNameFormatting();

  const sortedSeedings = useMemo(() => {
    return [...seedings].sort((a, b) => {
      // If both have seeds, sort by seed number (ascending)
      if (a.seedNumber !== null && b.seedNumber !== null) {
        return a.seedNumber - b.seedNumber;
      }
      // If only one has a seed, seeded player comes first
      if (a.seedNumber !== null && b.seedNumber === null) {
        return -1;
      }
      if (a.seedNumber === null && b.seedNumber !== null) {
        return 1;
      }
      // If neither has seeds, sort by pointsAtSeeding (descending, treating null as 0)
      const aPoints = a.pointsAtSeeding ?? 0;
      const bPoints = b.pointsAtSeeding ?? 0;
      return bPoints - aPoints;
    });
  }, [seedings]);

  return (
    <div className="space-y-4">
      {sortedSeedings.length === 0 ? (
        <Panel>
          <PanelContent className="flex flex-col items-center justify-center py-12">
            <DocumentTextIcon className="text-muted mb-4 h-12 w-12" />
            <p className="text-muted text-center">{t('noSeedings')}</p>
          </PanelContent>
        </Panel>
      ) : (
        <Panel>
          <PanelContent className="p-0">
            <div>
              {sortedSeedings.map((seeding, index) => {
                const prefix = seeding.seedNumber ? `[${seeding.seedNumber}]` : '';
                const player = seeding.player
                  ? {
                      id: seeding.player.id,
                      username: seeding.player.username,
                      first_name: seeding.player.firstName,
                      last_name: seeding.player.lastName,
                      current_rating: seeding.player.currentRating,
                      profile_photo: seeding.player.profilePhoto,
                    }
                  : null;

                const href = player && playerHref?.(player.id);
                const label = player
                  ? resolveParticipantLabel({
                      entry: isTeamDivision ? seeding.team : undefined,
                      player: { ...player, alias: seeding.player.alias },
                      isJapanese,
                    })
                  : '';
                return (
                  <div key={index} className="flex items-center gap-2 p-2">
                    <div className="w-8 text-center text-sm font-bold">{prefix}</div>
                    <div className="min-w-0 flex-1">
                      {player && href ? (
                        <Link href={href} className="flex min-h-11 min-w-0 items-center gap-2">
                          <Avatar name={label} photoUrl={player.profile_photo} />
                          <span className="min-w-0 flex-1">
                            <span className={`block text-sm font-medium ${NAME_WRAP}`}>
                              <NameText name={label} />
                            </span>
                            {isTeamDivision && seeding.team?.roster && (
                              <span className="text-muted block text-xs whitespace-pre-line">
                                {seeding.team.roster}
                              </span>
                            )}
                          </span>
                          {!isTeamDivision && showRatings && player.current_rating !== null && (
                            <Numeric>{player.current_rating}</Numeric>
                          )}
                        </Link>
                      ) : player ? (
                        isTeamDivision ? (
                          <div className="min-w-0">
                            <div className={`text-sm leading-tight font-medium ${NAME_WRAP}`}>
                              {resolveParticipantLabel({
                                entry: seeding.team,
                                player: {
                                  first_name: player.first_name,
                                  last_name: player.last_name,
                                  alias: seeding.player.alias,
                                },
                                isJapanese,
                              })}
                            </div>
                            {seeding.team?.roster && (
                              <div className="text-muted text-xs whitespace-pre-line">
                                {seeding.team.roster}
                              </div>
                            )}
                          </div>
                        ) : (
                          <PlayerCard
                            player={player}
                            size="compact"
                            clickable={true}
                            showUsername={false}
                            showRating={showRatings}
                            className="gap-2"
                          />
                        )
                      ) : (
                        <div className="text-xs">{tMatches('tbd')}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </PanelContent>
        </Panel>
      )}
    </div>
  );
}
